import { formatUTCDate } from 'helpers/DateHelpers';

// Single source of truth for key mappings
const keyDisplayMap: Record<string, string> = {
  enrollment_application_id: 'Enrollment Application Id',
  enrollment_period_start: 'Enrollment Period Start',
  enrollment_period_end: 'Enrollment Period End',
  max_days_since_enrollment: 'Max Days Since Enrollment',
  min_days_since_enrollment: 'Min Days Since Enrollment',
  min_days_since_last_commission: 'Min Days Since Last Commission',
  days: 'Days',
  post_campaign_purchase_window_days: 'Post Campaign Purchase Window Days',
};

// Helper to create reverse mapping
const createReverseKeyMap = () => {
  return Object.fromEntries(Object.entries(keyDisplayMap).map(([key, value]) => [value, key]));
};

// Returns campaign status based on start and end dates
export const getCampaignStatus = (campaignStartDate: string, campaignEndDate: string) => {
  const today = formatUTCDate(new Date(), 'yyy-MM-dd HH:mm');
  const startDate = formatUTCDate(new Date(campaignStartDate), 'yyy-MM-dd HH:mm');
  const endDate = formatUTCDate(new Date(campaignEndDate), 'yyy-MM-dd HH:mm');

  if (endDate < today) {
    return 'past';
  } else if (startDate <= today && endDate >= today) {
    return 'present';
  } else if (startDate > today) {
    return 'future';
  }
};

// Converts JSON config to user-friendly display
export const jsonToFriendlyDisplay = (jsonString: string): string => {
  try {
    const config = JSON.parse(jsonString);
    if (Object.keys(config).length === 0) {
      return '';
    }

    return Object.entries(config)
      .map(([key, value]) => {
        const displayKey = keyDisplayMap[key] || key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
        return `${displayKey}: ${value}`;
      })
      .join('\n');
  } catch {
    return jsonString; // Return original if parsing fails
  }
};

// Converts user-friendly display back to JSON
export const friendlyDisplayToJson = (friendlyString: string): string => {
  if (!friendlyString.trim()) {
    return '{}';
  }

  try {
    const keyMap = createReverseKeyMap();

    const config: Record<string, string> = {};
    const lines = friendlyString.split('\n').filter(line => line.trim());

    lines.forEach(line => {
      const colonIndex = line.indexOf(': ');
      if (colonIndex !== -1) {
        const displayKey = line.substring(0, colonIndex).trim();
        const value = line.substring(colonIndex + 1).trim();
        const jsonKey = keyMap[displayKey] || displayKey.toLowerCase().replace(/\s+/g, '_');
        config[jsonKey] = value;
      }
    });

    return JSON.stringify(config, null, 2);
  } catch {
    return '{}';
  }
};

// Formats Date to ISO string without milliseconds
export const toISOStringWithoutMs = (date: Date): string => {
  return date.toISOString().slice(0, 19) + 'Z';
};
